import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabDescriptor, CommandeBCI } from '../types';
import { buildCommandeTabs, computeDefaultTabIndex } from '../domain/commandeTabs';

// Hook mince : la logique (gating / onglet par defaut) vit dans domain/commandeTabs.
// Ici, uniquement le cablage de l'onglet actif a l'URL (?tab=<index>), comme la case acheteur.
export const useCommandeTabs = (commande: CommandeBCI | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const descriptors = useMemo<TabDescriptor[]>(
    () => (commande ? buildCommandeTabs(commande) : []),
    [commande],
  );

  const defaultIndex = useMemo(
    () => (commande ? computeDefaultTabIndex(descriptors, commande) : 0),
    [descriptors, commande],
  );

  const tabParam = searchParams.get('tab');
  const activeIndex = useMemo(() => {
    if (tabParam !== null) {
      const parsed = parseInt(tabParam, 10);
      if (!Number.isNaN(parsed) && parsed >= 0 && parsed < descriptors.length) {
        const target = descriptors[parsed];
        if (target && !target.disabled && !target.inactive) return parsed;
      }
    }
    return defaultIndex;
  }, [tabParam, defaultIndex, descriptors]);

  const setActiveIndex = useCallback(
    (index: number) => {
      const target = descriptors[index];
      if (!target || target.disabled || target.inactive) return;
      const next = new URLSearchParams(searchParams);
      next.set('tab', String(index));
      setSearchParams(next, { replace: true });
    },
    [descriptors, searchParams, setSearchParams],
  );

  return {
    descriptors,
    activeIndex,
    activeTab: descriptors[activeIndex] ?? null,
    setActiveIndex,
  };
};
